import { memo } from 'react';
import { css } from '@emotion/react';
import { EdgeLabelRenderer, EdgeProps, getBezierPath } from 'reactflow';
import { typography } from '@uniquegood/realworld-studio-design';

import type { MissionEdgeType } from '../types';

interface MissionDependencyEdgeData {
  label?: string;
  edgeType?: MissionEdgeType;
}

const EDGE_COLORS: Record<MissionEdgeType, string> = {
  reveal: '#22c55e',
  unlock: '#3b82f6',
  accomplish: '#f59e0b',
  lock: '#ef4444'
};

const EDGE_DASHED: Record<MissionEdgeType, boolean> = {
  reveal: true,
  unlock: true,
  accomplish: false,
  lock: true
};

function MissionDependencyEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style
}: EdgeProps<MissionDependencyEdgeData>) {
  const edgeType = data?.edgeType ?? 'unlock';
  const edgeColor = EDGE_COLORS[edgeType];
  const isDashed = EDGE_DASHED[edgeType];

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition
  });

  return (
    <>
      <defs>
        <marker
          id={`mdep-arrow-${id}`}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <path d="M0,0 L10,3.5 L0,7 Z" fill={edgeColor} />
        </marker>
      </defs>
      <path
        id={id}
        d={edgePath}
        markerEnd={`url(#mdep-arrow-${id})`}
        style={{
          ...style,
          stroke: edgeColor,
          strokeWidth: 2,
          strokeDasharray: isDashed ? '6 4' : undefined,
          fill: 'none'
        }}
      />
      <EdgeLabelRenderer>
        <div
          css={LabelStyle}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            color: edgeColor,
            borderColor: edgeColor
          }}
        >
          {data?.label ?? ''}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default memo(MissionDependencyEdge);

const LabelStyle = css`
  position: absolute;
  pointer-events: none;
  font-size: 10px;
  font-weight: ${typography.weight.bold};
  letter-spacing: 0.04em;
  background-color: #fff;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid;
`;
